/**
 * HeatmapContainer - Bubble map using react-leaflet CircleMarkers
 * Size = conversation count, Color = sentiment
 */

import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [22.5, 82.0];
const DEFAULT_ZOOM = 5;

/** Sentiment score → bubble fill color */
const bubbleColor = (score) => {
  if (score == null) return '#9ca3af';
  if (score >= 0.1) return '#22c55e';
  if (score >= -0.1) return '#eab308';
  return '#ef4444';
};

/** Sentiment score → label */
const sentimentLabel = (score) => {
  if (score == null) return 'N/A';
  if (score >= 0.5) return 'Positive';
  if (score >= 0.1) return 'Slightly Positive';
  if (score >= -0.1) return 'Neutral';
  if (score >= -0.5) return 'Slightly Negative';
  return 'Negative';
};

/** Sqrt-scaled radius: min 6, max 30 */
const bubbleRadius = (count, maxCount) => {
  if (maxCount <= 0) return 8;
  return Math.max(6, Math.min(30, Math.sqrt(count / maxCount) * 30));
};

/** Auto-fit bounds to data */
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [map, points]);
  return null;
};

const HeatmapContainer = ({ data = [], height = 480, onCityClick }) => {
  /** Aggregate raw points by city */
  const cityData = useMemo(() => {
    const map = {};
    data.filter(p => p.latitude && p.longitude).forEach(p => {
      const key = p.city || `${p.latitude.toFixed(2)},${p.longitude.toFixed(2)}`;
      if (!map[key]) {
        map[key] = { city: p.city || key, lat: p.latitude, lng: p.longitude, count: 0, sentimentSum: 0, sentimentCount: 0 };
      }
      map[key].count += 1;
      if (p.sentiment_score != null) {
        map[key].sentimentSum += p.sentiment_score;
        map[key].sentimentCount += 1;
      }
    });
    return Object.values(map).map(c => ({
      ...c,
      avgSentiment: c.sentimentCount > 0 ? c.sentimentSum / c.sentimentCount : null,
    }));
  }, [data]);

  const maxCount = useMemo(() => Math.max(1, ...cityData.map(c => c.count)), [cityData]);

  return (
    <div className="relative" style={{ height }} aria-label="Conversation bubble map showing geographic distribution">
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        className="w-full h-full rounded-lg overflow-hidden z-0"
        style={{ height: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {cityData.map(c => (
          <CircleMarker
            key={c.city}
            center={[c.lat, c.lng]}
            radius={bubbleRadius(c.count, maxCount)}
            fillColor={bubbleColor(c.avgSentiment)}
            fillOpacity={0.7}
            weight={1.5}
            color="#fff"
            eventHandlers={{ click: () => onCityClick?.(c.city) }}
          >
            <Tooltip>
              <div className="text-xs">
                <strong>{c.city}</strong><br />
                {c.count} conversation{c.count !== 1 ? 's' : ''}<br />
                {sentimentLabel(c.avgSentiment)}
                {c.avgSentiment != null ? ` (${c.avgSentiment.toFixed(2)})` : ''}
              </div>
            </Tooltip>
          </CircleMarker>
        ))}

        <FitBounds points={cityData} />
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white/90 rounded-lg px-3 py-2 shadow-md z-[1000]">
        <p className="text-[10px] font-medium text-gray-500 mb-1">Sentiment</p>
        <div className="flex items-center gap-3 text-[10px] text-gray-600">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> Positive</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-500 inline-block" /> Neutral</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> Negative</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400 inline-block" /> N/A</span>
        </div>
        <p className="text-[9px] text-gray-400 mt-1">Size = conversation volume</p>
      </div>

      {data.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg z-[500]">
          <p className="text-sm text-gray-500">No location data available</p>
        </div>
      )}
    </div>
  );
};

export default React.memo(HeatmapContainer);
